(function () {
  const SECTION_TYPE = 'main-collection-product-grid';
  const CARET_ICON =
    '<svg width="16" height="16" viewBox="0 0 16 16" class="icon icon-caret" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const INTERNAL_PARAMS = ['page', 'segment_page', 'section_id', 'sections', 'segment_fetch'];
  let cachedCatalog = null;
  let cachedCatalogKey = null;

  function getFilterSearchParams(search) {
    const raw =
      typeof search === 'string'
        ? search.startsWith('?')
          ? search.slice(1)
          : search
        : search instanceof URLSearchParams
          ? search.toString()
          : '';
    if (!raw) return '';

    const params = new URLSearchParams(raw);
    INTERNAL_PARAMS.forEach((key) => params.delete(key));
    return params.toString();
  }

  function getCurrentFilterSearchParams() {
    if (window.FacetFiltersForm?.searchParamsPrev) {
      return getFilterSearchParams(FacetFiltersForm.searchParamsPrev);
    }
    return getFilterSearchParams(window.location.search);
  }

  function buildSearchParams(pageNumber, baseSearch) {
    const filterParams = getFilterSearchParams(baseSearch ?? getCurrentFilterSearchParams());
    const params = new URLSearchParams(filterParams);
    if (pageNumber > 1) {
      params.set('page', String(pageNumber));
    } else {
      params.delete('page');
    }
    return params.toString();
  }

  function buildPageUrl(pageNumber) {
    const built = buildSearchParams(pageNumber);
    return `${window.location.pathname}${built ? `?${built}` : ''}`;
  }

  function getContainer() {
    return document.getElementById('ProductGridContainer');
  }

  function isSegmentPagination(container) {
    return container && container.dataset.segmentPagination === 'true';
  }

  function getPageFromParams(searchParams) {
    const params = searchParams instanceof URLSearchParams ? searchParams : new URLSearchParams(searchParams);
    if (params.has('page')) {
      return parseInt(params.get('page') || '1', 10);
    }
    if (params.has('segment_page')) {
      return parseInt(params.get('segment_page') || '1', 10);
    }
    return 1;
  }

  function getCurrentPage() {
    const url = new URL(window.location.href);
    const grid = getContainer()?.querySelector('#product-grid');
    return getPageFromParams(url.searchParams) || parseInt(grid?.dataset.displayPage || '1', 10);
  }

  function getGridListClass(container) {
    return (
      container?.dataset.gridListClass || 'collection-product-list collection-product-list--1-mobile load-more-grid'
    );
  }

  function formatProductCount(count) {
    const container = getContainer();
    if (!container) return String(count);
    const oneTpl = container.dataset.countOne;
    const otherTpl = container.dataset.countOther;
    if (oneTpl && otherTpl) {
      const tpl = count === 1 ? oneTpl : otherTpl;
      return tpl.replace('__COUNT__', String(count));
    }
    return String(count);
  }

  function updateProductCount(count) {
    const text = formatProductCount(count);
    const desktop = document.getElementById('ProductCountDesktop');
    const mobile = document.querySelector('.mobile-facets__count');
    const hidden = document.getElementById('ProductCount');
    if (desktop) desktop.textContent = text;
    if (mobile) mobile.textContent = text;
    if (hidden) hidden.textContent = text;
    const container = getContainer();
    if (container) container.dataset.accessibleTotal = String(count);
  }

  function parseCatalogFromRoot(root) {
    const template = root.querySelector('#segment-product-catalog');
    const scope = template ? template.content : root;
    const cards = scope.querySelectorAll('.collection-product-card');
    const meta = scope.querySelector('[data-segment-pagination-meta]');
    return {
      cards: Array.from(cards),
      totalPages: meta ? parseInt(meta.dataset.filteredPages, 10) || 0 : 0,
      accessibleTotal: (() => {
        if (!meta) return cards.length;
        const parsed = parseInt(meta.dataset.accessibleTotal, 10);
        if (Number.isFinite(parsed) && parsed > 0) return parsed;
        return cards.length;
      })(),
    };
  }

  function readCatalogFromTemplate(container) {
    const template = container.querySelector('#segment-product-catalog');
    if (!template) return null;
    const catalog = parseCatalogFromRoot(container);
    return catalog.cards.length ? catalog : null;
  }

  function readCatalogFromDom(container) {
    const fromTemplate = readCatalogFromTemplate(container);
    if (fromTemplate) return fromTemplate;

    const grid = container.querySelector('#product-grid');
    const meta = grid?.querySelector('[data-segment-pagination-meta]');
    if (!meta) return null;

    const cards = grid.querySelectorAll('.collection-product-card');
    if (!cards.length) return null;

    return {
      cards: Array.from(cards),
      totalPages: parseInt(meta.dataset.filteredPages, 10) || 0,
      accessibleTotal: (() => {
        const parsed = parseInt(meta.dataset.accessibleTotal, 10);
        if (Number.isFinite(parsed) && parsed > 0) return parsed;
        return cards.length;
      })(),
    };
  }

  async function fetchCatalog(container) {
    const sectionId = container.querySelector('[data-id]')?.dataset.id;
    const filterParams = getCurrentFilterSearchParams();
    const basePath = `${window.location.pathname}${filterParams ? `?${filterParams}` : ''}`;
    const base = new URL(basePath, window.location.origin);
    base.searchParams.set('segment_fetch', 'all');

    if (sectionId) {
      const sectionUrl = new URL(base);
      sectionUrl.searchParams.set('section_id', sectionId);
      const response = await fetch(sectionUrl.toString());
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const catalog = parseCatalogFromRoot(doc);
      if (catalog.cards.length) return catalog;
    }

    const sectionsUrl = new URL(base);
    sectionsUrl.searchParams.set('sections', SECTION_TYPE);
    const response = await fetch(sectionsUrl.toString());
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      const data = await response.json();
      const sectionHtml = data[SECTION_TYPE] || Object.values(data)[0];
      if (sectionHtml) {
        const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
        const catalog = parseCatalogFromRoot(doc);
        if (catalog.cards.length) return catalog;
      }
    }

    return null;
  }

  async function ensureCatalog(container, forceRefresh) {
    const cacheKey = getCurrentFilterSearchParams();
    if (!forceRefresh && cachedCatalog && cachedCatalogKey === cacheKey) return cachedCatalog;

    const fromDom = readCatalogFromDom(container);
    if (!forceRefresh && fromDom?.cards.length) {
      cachedCatalog = fromDom;
      cachedCatalogKey = cacheKey;
      return cachedCatalog;
    }

    const fetched = await fetchCatalog(container);
    if (fetched?.cards.length) {
      cachedCatalog = fetched;
      cachedCatalogKey = cacheKey;
      return fetched;
    }

    if (fromDom?.cards.length) {
      cachedCatalog = fromDom;
      cachedCatalogKey = cacheKey;
      return fromDom;
    }

    return null;
  }

  function notifyBssProducts() {
    if (typeof BSS_B2B !== 'undefined' && BSS_B2B?.observer?.productSubject?.notifyObserver) {
      BSS_B2B.observer.productSubject.notifyObserver('ProductMutate', 'ProductPriceMutate');
    }
  }

  function prepareGridForClientPage(container, page) {
    const emptyState = container.querySelector('.title-wrapper-empty');
    const existingGrid = container.querySelector('#product-grid');
    const sectionId = existingGrid?.dataset.id || container.querySelector('[data-id]')?.dataset.id || '';
    const listClasses = existingGrid?.className || getGridListClass(container);

    if (emptyState || !existingGrid) {
      container.innerHTML = `
        <div class="collection collection-product loading">
          <div class="loading-overlay">
            <div class="loading-overlay__spinner">
              <svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
              </svg>
            </div>
          </div>
          <ul
            id="product-grid"
            data-id="${sectionId}"
            data-segment-deferred="true"
            data-display-page="${page}"
            class="${listClasses}"
          ></ul>
        </div>
      `;
      return;
    }

    if (!existingGrid.querySelector('.collection-product-card')) {
      existingGrid.dataset.segmentDeferred = 'true';
      existingGrid.dataset.displayPage = String(page);
      existingGrid.closest('.collection')?.classList.add('loading');
    }
  }

  function buildPaginationParts(currentPage, totalPages) {
    const parts = [];
    const total = Math.max(1, totalPages);
    const current = Math.min(Math.max(1, currentPage), total);

    const addPage = (page) => {
      parts.push({ title: String(page), page, isLink: page !== current, isEllipsis: false });
    };
    const addEllipsis = () => {
      parts.push({ title: '…', page: null, isLink: false, isEllipsis: true });
    };

    if (total <= 7) {
      for (let page = 1; page <= total; page += 1) addPage(page);
      return parts;
    }

    addPage(1);

    if (current > 3) addEllipsis();

    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    for (let page = start; page <= end; page += 1) addPage(page);

    if (current < total - 2) addEllipsis();

    addPage(total);
    return parts;
  }

  function renderPagination(container, currentPage, totalPages) {
    if (totalPages <= 1) return;

    const collectionEl = container.querySelector('.collection');
    if (!collectionEl) return;

    let wrapper = collectionEl.querySelector('.pagination-wrapper');
    if (!wrapper) {
      wrapper = document.createElement('div');
      wrapper.className = 'pagination-wrapper no-js-block';
      collectionEl.appendChild(wrapper);
    }

    const items = [];

    if (currentPage > 1) {
      items.push(
        `<li><a href="${buildPageUrl(currentPage - 1)}" class="pagination__item--prev pagination__item-arrow link motion-reduce" aria-label="Previous">${CARET_ICON}</a></li>`,
      );
    }

    buildPaginationParts(currentPage, totalPages).forEach((part) => {
      if (part.isEllipsis) {
        items.push(`<li><span class="pagination__item">${part.title}</span></li>`);
        return;
      }

      if (part.isLink) {
        items.push(
          `<li><a href="${buildPageUrl(part.page)}" class="pagination__item link" aria-label="Page ${part.title}"><span title="${part.title}">${part.title}</span></a></li>`,
        );
      } else {
        items.push(
          `<li><span class="pagination__item pagination__item--current" aria-current="page" aria-label="Page ${part.title}">${part.title}</span></li>`,
        );
      }
    });

    if (currentPage < totalPages) {
      items.push(
        `<li><a href="${buildPageUrl(currentPage + 1)}" class="pagination__item--next pagination__item-arrow link motion-reduce" aria-label="Next">${CARET_ICON}</a></li>`,
      );
    }

    wrapper.innerHTML = `<nav class="pagination" role="navigation" aria-label="Pagination"><ul class="pagination__list list-unstyled" role="list">${items.join('')}</ul></nav>`;
  }

  function renderSlice(container, page, catalog, perPage) {
    const grid = container.querySelector('#product-grid');
    if (!grid || !catalog || !catalog.cards.length) return false;

    const totalPages = catalog.totalPages || Math.max(1, Math.ceil(catalog.cards.length / perPage));
    const start = (page - 1) * perPage;
    const slice = catalog.cards.slice(start, start + perPage);
    if (!slice.length) return false;

    const fragment = document.createDocumentFragment();
    slice.forEach((card) => fragment.appendChild(card.cloneNode(true)));
    grid.replaceChildren(fragment);
    grid.removeAttribute('data-segment-deferred');

    const collectionEl = grid.closest('.collection');
    if (collectionEl) collectionEl.classList.remove('loading');

    renderPagination(container, page, totalPages);

    if (typeof colorSwatches === 'function') colorSwatches();
    if (typeof loadReviewsIoRatingSnippets === 'function') loadReviewsIoRatingSnippets();
    notifyBssProducts();

    if (window.FacetFiltersForm) {
      FacetFiltersForm.searchParamsPrev = buildSearchParams(page);
    }

    document.dispatchEvent(new Event('pagination:updated'));

    return true;
  }

  async function goToPage(page, options) {
    const container = getContainer();
    if (!isSegmentPagination(container)) return false;

    const perPage = parseInt(container.dataset.productsPerPage, 10) || 9;
    let catalog = await ensureCatalog(container, options?.forceRefresh);

    if (!catalog?.cards.length && page > 1) {
      catalog = await ensureCatalog(container, true);
    }

    if (!catalog?.cards.length) return false;

    const collectionEl = container.querySelector('.collection');
    if (collectionEl) collectionEl.classList.add('loading');

    let rendered = renderSlice(container, page, catalog, perPage);

    if (!rendered && page > 1) {
      catalog = await ensureCatalog(container, true);
      if (catalog?.cards.length) {
        rendered = renderSlice(container, page, catalog, perPage);
      }
    }

    if (collectionEl) collectionEl.classList.remove('loading');
    return rendered;
  }

  async function initSegmentPagination() {
    const container = getContainer();
    if (!isSegmentPagination(container)) return;

    const page = getCurrentPage();
    const serverTotal = parseInt(container.dataset.accessibleTotal, 10) || 0;

    if (page > 1 || serverTotal === 0) {
      const catalog = await ensureCatalog(container);
      if (catalog?.accessibleTotal) {
        updateProductCount(catalog.accessibleTotal);
      }
    }

    if (page <= 1) return;

    prepareGridForClientPage(container, page);
    await goToPage(page);
  }

  function isPaginationLink(target) {
    const container = getContainer();
    if (!isSegmentPagination(container)) return null;

    const link = target.closest('a[href]');
    if (!link || !container.contains(link)) return null;
    if (!link.closest('.pagination__list')) return null;
    return link;
  }

  async function handlePaginationClick(event) {
    const link = isPaginationLink(event.target);
    if (!link) return;

    const container = getContainer();
    if (!isSegmentPagination(container)) return;

    event.preventDefault();
    event.stopPropagation();

    const url = new URL(link.href, window.location.origin);
    const page = getPageFromParams(url.searchParams);
    const searchParams = buildSearchParams(page);
    const nextUrl = `${window.location.pathname}${searchParams ? `?${searchParams}` : ''}`;

    if (window.FacetFiltersForm) {
      FacetFiltersForm.searchParamsPrev = searchParams;
    }

    history.pushState({ searchParams }, '', nextUrl);

    const success = await goToPage(page);
    if (!success) {
      window.location.assign(nextUrl);
      return;
    }

    const anchor = document.getElementById('ProductGridContainer');
    if (anchor) {
      const y = anchor.getBoundingClientRect().top + window.pageYOffset - 90;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }

  function handlePopState(event) {
    const container = getContainer();
    if (!isSegmentPagination(container)) return;

    const searchParams = event.state?.searchParams ?? window.location.search.slice(1);
    if (window.FacetFiltersForm) {
      FacetFiltersForm.searchParamsPrev = searchParams;
    }
    const page = getPageFromParams(searchParams);
    const forceRefresh = getFilterSearchParams(searchParams) !== cachedCatalogKey;

    if (page > 1) {
      prepareGridForClientPage(container, page);
    }

    goToPage(page, { forceRefresh }).then((success) => {
      if (!success) {
        window.location.assign(`${window.location.pathname}${searchParams ? `?${searchParams}` : ''}`);
      }
    });
  }

  function isPageOnlyChange(prevParams, nextParams) {
    const prevPage = String(getPageFromParams(prevParams));
    const nextPage = String(getPageFromParams(nextParams));
    return getFilterSearchParams(prevParams) === getFilterSearchParams(nextParams) && prevPage !== nextPage;
  }

  function handleFacetPageChange(searchParams, updateURLHash) {
    const container = getContainer();
    if (!isSegmentPagination(container)) return false;
    if (!window.FacetFiltersForm || !isPageOnlyChange(FacetFiltersForm.searchParamsPrev, searchParams)) {
      return false;
    }

    const page = getPageFromParams(searchParams);

    if (updateURLHash) {
      history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams ? `?${searchParams}` : ''}`);
    }

    FacetFiltersForm.searchParamsPrev = searchParams;

    if (page > 1) {
      prepareGridForClientPage(container, page);
    }

    goToPage(page).then((success) => {
      if (!success) {
        window.location.assign(`${window.location.pathname}${searchParams ? `?${searchParams}` : ''}`);
      }
    });
    return true;
  }

  window.CollectionSegmentPagination = {
    init: initSegmentPagination,
    goToPage,
    handleFacetPageChange,
    updateProductCount,
    refresh: function () {
      cachedCatalog = null;
      cachedCatalogKey = null;
      const container = getContainer();
      if (!isSegmentPagination(container)) return;

      const page = getCurrentPage();
      if (page > 1) {
        prepareGridForClientPage(container, page);
        goToPage(page, { forceRefresh: true });
      } else {
        ensureCatalog(container, true);
      }
    },
    clearCache: function () {
      cachedCatalog = null;
      cachedCatalogKey = null;
    },
  };

  document.addEventListener('click', handlePaginationClick, true);
  window.addEventListener('popstate', handlePopState);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSegmentPagination);
  } else {
    initSegmentPagination();
  }
})();
